import { createFileRoute } from "@tanstack/react-router";
import { Fragment, useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Mail,
  Send,
  Play,
  Eye,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ShieldAlert,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AdminGuard } from "@/lib/auth/AdminGuard";
import { getErrorMessage } from "@/lib/api/client";
import { formatDate } from "@/lib/utils";
import {
  getMonthlySummaryConfig,
  updateMonthlySummaryConfig,
  getMonthlySummaryPreview,
  testSendMonthlySummary,
  runMonthlySummaryNow,
  listMonthlySummaryRuns,
  getPncStatus,
  getHorsPolitiqueConfig,
  updateHorsPolitiqueConfig,
  getHorsPolitiquePreview,
  testSendHorsPolitique,
  listHorsPolitiqueRuns,
  type AlertRun,
} from "@/lib/api/alerts";

export const Route = createFileRoute("/_dashboard/admin/alerts")({
  component: AlertsPage,
});

function statusBadge(status: string) {
  switch (status) {
    case "ok":
      return <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/25">Envoyé</Badge>;
    case "partial":
      return <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/25">Partiel</Badge>;
    case "skipped":
      return <Badge className="bg-slate-500/15 text-slate-500 border-slate-500/25">Ignoré</Badge>;
    case "error":
      return <Badge className="bg-red-500/15 text-red-600 border-red-500/25">Erreur</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

/** Statut d'un destinataire, dont le vocabulaire diffère de celui de l'exécution. */
function recipientBadge(status: string) {
  switch (status) {
    case "sent":
      return <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/25">Envoyé</Badge>;
    case "skipped":
      return <Badge className="bg-slate-500/15 text-slate-500 border-slate-500/25">Non envoyé</Badge>;
    case "error":
      return <Badge className="bg-red-500/15 text-red-600 border-red-500/25">Erreur</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

// Erreurs d'abord, puis non-envoyés : ce qu'on vient vérifier passe en tête.
const RECIPIENT_ORDER: Record<string, number> = { error: 0, skipped: 1, sent: 2 };

function AlertsPage() {
  const queryClient = useQueryClient();

  const [enabled, setEnabled] = useState(false);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [hour, setHour] = useState(8);
  const [testEmail, setTestEmail] = useState("");
  const [testBalise, setTestBalise] = useState("");
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [openRunId, setOpenRunId] = useState<number | null>(null);

  // Alerte "cotation hors politique"
  const [hpEnabled, setHpEnabled] = useState(false);
  const [hpRecipients, setHpRecipients] = useState("");
  const [hpTestEmail, setHpTestEmail] = useState("");
  const [hpPreviewHtml, setHpPreviewHtml] = useState<string | null>(null);

  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ["alert-config", "monthly-summary"],
    queryFn: getMonthlySummaryConfig,
  });

  const { data: pncStatus } = useQuery({
    queryKey: ["alert-pnc-status"],
    queryFn: getPncStatus,
  });

  const { data: runs, isLoading: runsLoading } = useQuery({
    queryKey: ["alert-runs", "monthly-summary"],
    queryFn: () => listMonthlySummaryRuns(20),
  });

  const { data: hpConfig, isLoading: hpConfigLoading } = useQuery({
    queryKey: ["alert-config", "hors-politique"],
    queryFn: getHorsPolitiqueConfig,
  });

  const { data: hpRuns, isLoading: hpRunsLoading } = useQuery({
    queryKey: ["alert-runs", "hors-politique"],
    queryFn: () => listHorsPolitiqueRuns(20),
  });

  useEffect(() => {
    if (config) {
      setEnabled(config.enabled);
      setDayOfMonth(config.day_of_month);
      setHour(config.hour);
    }
  }, [config]);

  useEffect(() => {
    if (hpConfig) {
      setHpEnabled(hpConfig.enabled);
      setHpRecipients((hpConfig.recipients || []).join("\n"));
    }
  }, [hpConfig]);

  const saveMutation = useMutation({
    mutationFn: () =>
      updateMonthlySummaryConfig({
        enabled,
        day_of_month: dayOfMonth,
        hour,
      }),
    onSuccess: () => {
      toast.success("Configuration enregistrée");
      queryClient.invalidateQueries({ queryKey: ["alert-config", "monthly-summary"] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const previewMutation = useMutation({
    mutationFn: () => getMonthlySummaryPreview(),
    onSuccess: (data) => {
      setPreviewHtml(data.html);
      toast.success(`Aperçu pour ${data.user.full_name || data.user.email}`);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const testMutation = useMutation({
    mutationFn: () => testSendMonthlySummary(testEmail, undefined, testBalise),
    onSuccess: (res) => {
      if (res.success) {
        toast.success(`Email de test envoyé à ${res.email}`);
      } else if (res.status === "skipped") {
        toast.warning("PNC non configuré — envoi ignoré");
      } else {
        // Sur 400, la réponse PNC mentionne souvent le nom de balise attendu.
        toast.error(res.detail || "Échec de l'envoi du test", { duration: 12000 });
      }
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const runNowMutation = useMutation({
    mutationFn: runMonthlySummaryNow,
    onSuccess: (res) => {
      toast.success(
        `Exécution ${res.period_label} : ${res.success_count} envoyé(s), ${res.skipped_count} ignoré(s), ${res.error_count} erreur(s)`
      );
      queryClient.invalidateQueries({ queryKey: ["alert-runs", "monthly-summary"] });
      queryClient.invalidateQueries({ queryKey: ["alert-config", "monthly-summary"] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const parseRecipients = (raw: string): string[] =>
    Array.from(
      new Set(
        raw
          .split(/[\n,;]+/)
          .map((e) => e.trim())
          .filter(Boolean)
      )
    );

  const hpSaveMutation = useMutation({
    mutationFn: () =>
      updateHorsPolitiqueConfig({
        enabled: hpEnabled,
        recipients: parseRecipients(hpRecipients),
      }),
    onSuccess: (data) => {
      toast.success("Alerte hors politique enregistrée");
      setHpRecipients((data.recipients || []).join("\n"));
      queryClient.invalidateQueries({ queryKey: ["alert-config", "hors-politique"] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const hpPreviewMutation = useMutation({
    mutationFn: getHorsPolitiquePreview,
    onSuccess: (data) => {
      setHpPreviewHtml(data.html);
      toast.success("Aperçu généré");
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const hpTestMutation = useMutation({
    mutationFn: () => testSendHorsPolitique(hpTestEmail),
    onSuccess: (res) => {
      if (res.success) {
        toast.success(`Email de test envoyé à ${res.email}`);
      } else if (res.status === "skipped") {
        toast.warning("PNC non configuré — envoi ignoré");
      } else {
        toast.error(res.detail || "Échec de l'envoi du test", { duration: 12000 });
      }
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const pncReady = pncStatus?.ready ?? false;

  return (
    <AdminGuard>
      <div className="flex flex-col min-h-screen">
        <Header
          title={
            <span className="flex items-center gap-2">
              <Mail className="h-5 w-5" /> Alertes & Mailing
            </span>
          }
          subtitle="Synthèse mensuelle d'activité aux responsables (via PNC)"
        />

        <div className="flex-1 p-6 space-y-6 max-w-5xl">
          {/* PNC status banner */}
          {pncStatus && !pncReady && (
            <Card className="border-amber-500/40 bg-amber-500/5">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-amber-700">
                    PNC non configuré — les envois réels sont inactifs (statut « ignoré »).
                  </p>
                  <ul className="mt-1 text-muted-foreground list-disc list-inside space-y-0.5">
                    {!pncStatus.apim_credentials && <li>Identifiants APIM manquants ({pncStatus.apim_env})</li>}
                    {!pncStatus.partner && <li>Code partenaire (PNC_PARTNER) manquant</li>}
                    {!pncStatus.model_code && <li>Code modèle email (PNC_EMAIL_MODEL_CODE) manquant</li>}
                    {!pncStatus.body_variable && <li>Nom de la balise du corps (PNC_EMAIL_BODY_VARIABLE) manquant</li>}
                  </ul>
                </div>
              </CardContent>
            </Card>
          )}
          {pncStatus && pncReady && (
            <Card className="border-emerald-500/40 bg-emerald-500/5">
              <CardContent className="p-4 flex items-center gap-3 text-sm">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                <span className="text-emerald-700 font-medium">
                  PNC configuré ({pncStatus.apim_env}) — {pncStatus.responsables_count} responsable(s) ciblé(s).
                </span>
              </CardContent>
            </Card>
          )}

          {/* Config card */}
          <Card>
            <CardContent className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">Synthèse mensuelle</h3>
                  <p className="text-sm text-muted-foreground">
                    Envoie chaque mois un résumé d'activité du mois précédent à chaque responsable, sur son périmètre.
                  </p>
                </div>
                {configLoading ? (
                  <Skeleton className="h-6 w-11" />
                ) : (
                  <Switch checked={enabled} onCheckedChange={setEnabled} />
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="day">Jour du mois (1-28)</Label>
                  <Input
                    id="day"
                    type="number"
                    min={1}
                    max={28}
                    value={dayOfMonth}
                    onChange={(e) => setDayOfMonth(Number(e.target.value))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hour">Heure (UTC, 0-23)</Label>
                  <Input
                    id="hour"
                    type="number"
                    min={0}
                    max={23}
                    value={hour}
                    onChange={(e) => setHour(Number(e.target.value))}
                  />
                </div>
              </div>

              {config?.last_run_at && (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  Dernière exécution : {formatDate(config.last_run_at)}
                  {config.last_run_month ? ` (mois ${config.last_run_month})` : ""}
                  {config.last_status ? ` — ${config.last_status}` : ""}
                </p>
              )}

              <div className="flex flex-wrap gap-3 pt-2">
                <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
                  Enregistrer
                </Button>
                <Button
                  variant="outline"
                  onClick={() => previewMutation.mutate()}
                  disabled={previewMutation.isPending}
                >
                  <Eye className="h-4 w-4 mr-2" /> Aperçu
                </Button>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" disabled={runNowMutation.isPending}>
                      <Play className="h-4 w-4 mr-2" /> Exécuter maintenant
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Exécuter la synthèse maintenant ?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Un email sera envoyé immédiatement à tous les responsables (si PNC est configuré).
                        Cela marque aussi le mois courant comme traité.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                      <AlertDialogAction onClick={() => runNowMutation.mutate()}>
                        Exécuter
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>

              {/* Test send */}
              <div className="border-t pt-4 space-y-2">
                <Label htmlFor="test-email">Envoyer un email de test</Label>
                <div className="flex flex-wrap gap-2 max-w-2xl">
                  <Input
                    id="test-email"
                    type="email"
                    placeholder="destinataire@laposte.fr"
                    className="flex-1 min-w-[220px]"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                  />
                  <Input
                    id="test-balise"
                    placeholder="Balise (optionnel, ex. CONTENU)"
                    className="flex-1 min-w-[200px]"
                    value={testBalise}
                    onChange={(e) => setTestBalise(e.target.value)}
                  />
                  <Button
                    variant="secondary"
                    onClick={() => testMutation.mutate()}
                    disabled={testMutation.isPending || !testEmail}
                  >
                    <Send className="h-4 w-4 mr-2" /> Tester
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Laisse la balise vide pour utiliser celle du <code>.env</code>. Renseigne-la pour
                  essayer un nom et lire la réponse PNC (utile pour découvrir le bon nom de balise).
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Preview */}
          {previewHtml && (
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-3">Aperçu de l'email</h3>
                <iframe
                  title="Aperçu email"
                  srcDoc={previewHtml}
                  className="w-full h-[600px] rounded-lg border bg-white"
                />
              </CardContent>
            </Card>
          )}

          {/* History */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold mb-3">Historique des exécutions</h3>
              {runsLoading ? (
                <Skeleton className="h-32 w-full" />
              ) : runs && runs.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8" />
                      <TableHead>Date</TableHead>
                      <TableHead>Période</TableHead>
                      <TableHead>Déclencheur</TableHead>
                      <TableHead className="text-right">Destinataires</TableHead>
                      <TableHead className="text-right">Envoyés</TableHead>
                      <TableHead className="text-right">Ignorés</TableHead>
                      <TableHead className="text-right">Erreurs</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {runs.map((run: AlertRun) => {
                      const recipients = [...(run.detail?.results ?? [])].sort(
                        (a, b) =>
                          (RECIPIENT_ORDER[a.status] ?? 9) - (RECIPIENT_ORDER[b.status] ?? 9) ||
                          a.email.localeCompare(b.email)
                      );
                      const open = openRunId === run.id;
                      return (
                        <Fragment key={run.id}>
                          <TableRow
                            className={recipients.length ? "cursor-pointer" : undefined}
                            onClick={() =>
                              recipients.length && setOpenRunId(open ? null : run.id)
                            }
                          >
                            <TableCell className="text-muted-foreground">
                              {recipients.length ? (
                                open ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )
                              ) : null}
                            </TableCell>
                            <TableCell>{formatDate(run.run_at)}</TableCell>
                            <TableCell>{run.detail?.period_label || "—"}</TableCell>
                            <TableCell className="capitalize">{run.triggered_by}</TableCell>
                            <TableCell className="text-right">{run.recipients_count}</TableCell>
                            <TableCell className="text-right">{run.success_count}</TableCell>
                            <TableCell className="text-right">{run.skipped_count}</TableCell>
                            <TableCell className="text-right">{run.error_count}</TableCell>
                            <TableCell>{statusBadge(run.status)}</TableCell>
                          </TableRow>
                          {open && (
                            <TableRow className="hover:bg-transparent">
                              <TableCell colSpan={9} className="bg-muted/40 p-0">
                                <div className="px-6 py-3 space-y-1.5">
                                  {recipients.map((r) => (
                                    <div
                                      key={r.email}
                                      className="flex items-center gap-3 text-sm flex-wrap"
                                    >
                                      {recipientBadge(r.status)}
                                      <span className="font-medium">{r.email}</span>
                                      {r.metrics && (
                                        <span className="text-muted-foreground tabular-nums">
                                          {r.metrics.simulations} simulation
                                          {r.metrics.simulations > 1 ? "s" : ""} ·{" "}
                                          {r.metrics.cotations} cotation
                                          {r.metrics.cotations > 1 ? "s" : ""} ·{" "}
                                          {r.metrics.proposals} propale
                                          {r.metrics.proposals > 1 ? "s" : ""}
                                          {r.metrics.hors_politique > 0 &&
                                            ` · ${r.metrics.hors_politique} hors politique`}
                                        </span>
                                      )}
                                      {r.detail && (
                                        <span className="text-muted-foreground">{r.detail}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  Aucune exécution pour le moment.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Hors politique — config card */}
          <Card>
            <CardContent className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    <ShieldAlert className="h-4 w-4 text-amber-600" />
                    Cotation hors politique tarifaire
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Envoie un email immédiatement dès qu'une cotation est enregistrée avec une marge
                    hors des bornes définies dans la politique tarifaire.
                  </p>
                </div>
                {hpConfigLoading ? (
                  <Skeleton className="h-6 w-11" />
                ) : (
                  <Switch checked={hpEnabled} onCheckedChange={setHpEnabled} />
                )}
              </div>

              <div className="space-y-2 max-w-2xl">
                <Label htmlFor="hp-recipients">Destinataires (un email par ligne)</Label>
                <Textarea
                  id="hp-recipients"
                  rows={4}
                  placeholder={"responsable@laposte.fr\ndfi@laposte.fr"}
                  value={hpRecipients}
                  onChange={(e) => setHpRecipients(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Sépare les adresses par un retour à la ligne, une virgule ou un point-virgule.
                  Tous les destinataires reçoivent le même email et se voient mutuellement (PNC ne gère pas de Cc dédié).
                </p>
              </div>

              {hpConfig?.last_run_at && (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  Dernière alerte : {formatDate(hpConfig.last_run_at)}
                  {hpConfig.last_status ? ` — ${hpConfig.last_status}` : ""}
                </p>
              )}

              <div className="flex flex-wrap gap-3 pt-2">
                <Button onClick={() => hpSaveMutation.mutate()} disabled={hpSaveMutation.isPending}>
                  Enregistrer
                </Button>
                <Button
                  variant="outline"
                  onClick={() => hpPreviewMutation.mutate()}
                  disabled={hpPreviewMutation.isPending}
                >
                  <Eye className="h-4 w-4 mr-2" /> Aperçu
                </Button>
              </div>

              {/* Test send */}
              <div className="border-t pt-4 space-y-2">
                <Label htmlFor="hp-test-email">Envoyer un email de test</Label>
                <div className="flex flex-wrap gap-2 max-w-2xl">
                  <Input
                    id="hp-test-email"
                    type="email"
                    placeholder="destinataire@laposte.fr"
                    className="flex-1 min-w-[220px]"
                    value={hpTestEmail}
                    onChange={(e) => setHpTestEmail(e.target.value)}
                  />
                  <Button
                    variant="secondary"
                    onClick={() => hpTestMutation.mutate()}
                    disabled={hpTestMutation.isPending || !hpTestEmail}
                  >
                    <Send className="h-4 w-4 mr-2" /> Tester
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Le test utilise une cotation hors politique récente si disponible, sinon des données d'exemple.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Hors politique — preview */}
          {hpPreviewHtml && (
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-3">Aperçu de l'alerte</h3>
                <iframe
                  title="Aperçu alerte hors politique"
                  srcDoc={hpPreviewHtml}
                  className="w-full h-[600px] rounded-lg border bg-white"
                />
              </CardContent>
            </Card>
          )}

          {/* Hors politique — history */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold mb-3">Historique des alertes hors politique</h3>
              {hpRunsLoading ? (
                <Skeleton className="h-32 w-full" />
              ) : hpRuns && hpRuns.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Simulateur</TableHead>
                      <TableHead className="text-right">Destinataires</TableHead>
                      <TableHead className="text-right">Envoyés</TableHead>
                      <TableHead className="text-right">Erreurs</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {hpRuns.map((run: AlertRun) => (
                      <TableRow key={run.id}>
                        <TableCell>{formatDate(run.run_at)}</TableCell>
                        <TableCell>{run.detail?.client || "—"}</TableCell>
                        <TableCell>{run.detail?.simulator || "—"}</TableCell>
                        <TableCell className="text-right">{run.recipients_count}</TableCell>
                        <TableCell className="text-right">{run.success_count}</TableCell>
                        <TableCell className="text-right">{run.error_count}</TableCell>
                        <TableCell>{statusBadge(run.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  Aucune alerte pour le moment.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminGuard>
  );
}
